import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import * as companyService from '../services/companyService';
import * as reportService from '../services/reportService';
import * as activityService from '../services/activityService';
import type { CompanyStats } from '../services/companyService';
import type { LeadershipReportData, BlockedRequirement, RequirementsTrendPoint, SprintOption } from '../services/reportService';

type TrendPoint = RequirementsTrendPoint;
import type { Activity } from '../services/activityService';

function PMDashboardV2(): React.ReactElement {
  const { user, company, token } = useAuth();
  const navigate = useNavigate();

  const [companyStats, setCompanyStats] = useState<CompanyStats | null>(null);
  const [leadershipData, setLeadershipData] = useState<LeadershipReportData | null>(null);
  const [blockedItems, setBlockedItems] = useState<BlockedRequirement[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [sprints, setSprints] = useState<SprintOption[]>([]);
  const [myTeamIds, setMyTeamIds] = useState<string[]>([]);
  const [projectSparkline, setProjectSparkline] = useState<TrendPoint[]>([]);
  const [projectReqTrends, setProjectReqTrends] = useState<Record<string, TrendPoint[]>>({});
  const [trendData, setTrendData] = useState<RequirementsTrendPoint[]>([]);
  const [trendDays, setTrendDays] = useState<30 | 90 | 'ytd'>(30);
  const [trendLoading, setTrendLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sparklineHover, setSparklineHover] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'my' | 'all'>(() => {
    const saved = localStorage.getItem('pm_dashboard_v2_view_mode');
    return saved === 'all' ? 'all' : 'my';
  });

  const [isActivityExpanded, setIsActivityExpanded] = useState(() => {
    const saved = localStorage.getItem('pm_dashboard_v2_activity_expanded');
    return saved !== null ? saved === 'true' : true;
  });

  const loadTrend = useCallback(async (days: 30 | 90 | 'ytd') => {
    if (!token) return;
    setTrendLoading(true);
    try {
      if (days === 'ytd') {
        const ytdStart = `${new Date().getFullYear()}-01-01`;
        const data = await reportService.getRequirementsCreatedTrend(0, token, ytdStart);
        setTrendData(data);
      } else {
        const data = await reportService.getRequirementsCreatedTrend(days, token);
        setTrendData(data);
      }
    } catch {
      // Non-critical
    } finally {
      setTrendLoading(false);
    }
  }, [token]);

  useEffect(() => {
    async function loadAll() {
      if (!token || !company?.id) return;
      setLoading(true);
      setError(null);

      try {
        const [statsResult, leadershipResult, blockedResult, activityResult, trendResult, projectSparkResult, projectReqTrendsResult, sprintsResult, teamsResult] = await Promise.allSettled([
          companyService.getCompanyStats(company.id, token),
          reportService.getLeadershipReport(token, { includeSubtasks: true }),
          reportService.getBlockedRequirements(token),
          activityService.getCompanyActivities({ limit: 20 }, token),
          trendDays === 'ytd'
            ? reportService.getRequirementsCreatedTrend(0, token, `${new Date().getFullYear()}-01-01`)
            : reportService.getRequirementsCreatedTrend(trendDays, token),
          reportService.getProjectsCreatedTrend(token),
          reportService.getProjectRequirementTrends(token),
          reportService.getAllSprints(token),
          reportService.getMyTeamIds(token),
        ]);

        if (statsResult.status === 'fulfilled') setCompanyStats(statsResult.value);
        if (leadershipResult.status === 'fulfilled') setLeadershipData(leadershipResult.value);
        if (blockedResult.status === 'fulfilled') setBlockedItems(blockedResult.value);
        if (activityResult.status === 'fulfilled') setActivities(activityResult.value);
        if (trendResult.status === 'fulfilled') setTrendData(trendResult.value);
        if (projectSparkResult.status === 'fulfilled') setProjectSparkline(projectSparkResult.value);
        if (projectReqTrendsResult.status === 'fulfilled') setProjectReqTrends(projectReqTrendsResult.value);
        if (sprintsResult.status === 'fulfilled') setSprints(sprintsResult.value);
        if (teamsResult.status === 'fulfilled') setMyTeamIds(teamsResult.value);

        const allFailed = [statsResult, leadershipResult, blockedResult, activityResult].every(
          (r) => r.status === 'rejected'
        );
        if (allFailed) setError('Failed to load dashboard data. Please try again.');
      } catch {
        setError('An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, company?.id]);

  const toggleActivityExpanded = () => {
    const next = !isActivityExpanded;
    setIsActivityExpanded(next);
    localStorage.setItem('pm_dashboard_v2_activity_expanded', String(next));
  };

  const getRelativeTime = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getDaysUntil = (dateStr: string): number => {
    const now = new Date();
    const target = new Date(dateStr);
    return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleViewModeChange = (mode: 'my' | 'all') => {
    setViewMode(mode);
    localStorage.setItem('pm_dashboard_v2_view_mode', mode);
  };

  // Filter projects based on viewMode
  const filteredProjects = leadershipData
    ? viewMode === 'my'
      ? leadershipData.projects.filter((p) => p.owner_id === user?.id)
      : leadershipData.projects
    : [];

  const filteredProjectIds = new Set(filteredProjects.map((p) => p.id));
  const filteredProjectCodes = new Set(filteredProjects.map((p) => p.project_code));

  // Recalculate totals from filtered projects
  const filteredTotals = filteredProjects.reduce(
    (acc, p) => {
      acc.total_requirements += p.total_requirements || 0;
      acc.board_todo += p.board_breakdown.todo || 0;
      acc.board_in_progress += p.board_breakdown.in_progress || 0;
      acc.board_blocked += p.board_breakdown.blocked || 0;
      acc.board_done += p.board_breakdown.done || 0;
      acc.board_done_not_worked += p.board_breakdown.done_not_worked || 0;
      return acc;
    },
    { total_requirements: 0, board_todo: 0, board_in_progress: 0, board_blocked: 0, board_done: 0, board_done_not_worked: 0 }
  );

  const completionRate =
    filteredTotals.total_requirements > 0
      ? Math.round((filteredTotals.board_done / filteredTotals.total_requirements) * 100)
      : 0;

  // Filter blocked items, sprints, activities by filtered projects
  const filteredBlockedItems = viewMode === 'all'
    ? blockedItems
    : blockedItems.filter((b) => filteredProjectCodes.has(b.project_code));

  const filteredActivities = viewMode === 'all'
    ? activities
    : activities.filter((a) => !a.project_code || filteredProjectCodes.has(a.project_code));

  const myTeamIdSet = new Set(myTeamIds);
  const filteredSprints = viewMode === 'all'
    ? sprints
    : sprints.filter((s) => filteredProjectIds.has(s.project_id) || (s.team_id && myTeamIdSet.has(s.team_id)));

  // Aggregate per-project trend data for filtered projects
  const filteredTrendData = (() => {
    if (viewMode === 'all') return trendData;
    const merged: Record<string, number> = {};
    for (const pid of filteredProjectIds) {
      const pts = projectReqTrends[pid];
      if (pts) {
        for (const pt of pts) {
          merged[pt.date] = (merged[pt.date] || 0) + pt.count;
        }
      }
    }
    return Object.entries(merged)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  })();

  // Aggregate project-creation sparkline for filtered projects
  const filteredProjectSparkline: TrendPoint[] = (() => {
    if (viewMode === 'all') return projectSparkline;
    // In "My Projects" mode, sum per-project trends to approximate project-level activity
    const merged: Record<string, number> = {};
    for (const pid of filteredProjectIds) {
      const pts = projectReqTrends[pid];
      if (pts) {
        for (const pt of pts) {
          merged[pt.date] = (merged[pt.date] || 0) + pt.count;
        }
      }
    }
    return Object.entries(merged)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  })();

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'done':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400';
      case 'in_progress':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400';
      case 'ready':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400';
      case 'draft':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
    }
  };

  const formatStatus = (status: string): string => {
    if (status === 'in_progress') return 'In Progress';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getProgressBarColor = (percent: number): string => {
    if (percent >= 75) return 'bg-green-500 dark:bg-green-400';
    if (percent >= 40) return 'bg-blue-500 dark:bg-blue-400';
    if (percent >= 15) return 'bg-yellow-500 dark:bg-yellow-400';
    return 'bg-gray-400 dark:bg-gray-500';
  };

  const barChartData = [
    { status: 'To Do', count: filteredTotals.board_todo },
    { status: 'In Progress', count: filteredTotals.board_in_progress },
    { status: 'Blocked', count: filteredTotals.board_blocked },
    { status: 'Done', count: filteredTotals.board_done },
  ].sort((a, b) => a.count - b.count);

  const sortedProjects = [...filteredProjects]
    .filter((p) => ownerFilter === 'all' || p.owner_name === ownerFilter)
    .sort((a, b) => b.progress_percent - a.progress_percent);

  const ownerOptions = Array.from(new Set(filteredProjects.map((p) => p.owner_name).filter(Boolean))).sort();

  const activeSprints = filteredSprints.filter((s) => s.status === 'active');

  if (loading) {
    return (
      <Layout>
        <div className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">PM Dashboard</h1>
                <p className="mt-2 text-blue-100">
                  Sprint-focused overview for {user?.first_name}
                </p>
              </div>
              <div className="flex items-center bg-white/15 rounded-lg p-0.5 border border-white/20">
                <button
                  onClick={() => handleViewModeChange('my')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    viewMode === 'my'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  My Projects
                </button>
                <button
                  onClick={() => handleViewModeChange('all')}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                    viewMode === 'all'
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  All Projects
                </button>
              </div>
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {/* KPI Row */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {/* Team Members */}
            <div onClick={() => navigate('/users')} className="bg-surface-primary overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <dt className="text-sm font-medium text-text-tertiary truncate">Team Members</dt>
                    <dd className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-text-primary">
                        {companyStats?.total_users ?? '--'}
                      </span>
                      {companyStats && companyStats.active_users !== undefined && (
                        <span className="text-xs text-text-tertiary">
                          {companyStats.active_users} active
                        </span>
                      )}
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Projects */}
            <div className="bg-surface-primary shadow rounded-lg" style={{ overflow: 'visible' }}>
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                      <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <dt className="text-sm font-medium text-text-tertiary truncate">Projects</dt>
                    <dd className="mt-1 flex items-baseline gap-3">
                      <div>
                        <span className="text-2xl font-semibold text-text-primary">
                          {viewMode === 'all' ? (companyStats?.active_projects ?? '--') : filteredProjects.length}
                        </span>
                        <span className="text-xs text-text-tertiary ml-1">{viewMode === 'all' ? 'Active' : 'Projects'}</span>
                      </div>
                      {viewMode === 'all' && companyStats && companyStats.done_projects > 0 && (
                        <div className="border-l border-border-primary pl-3">
                          <span className="text-2xl font-semibold text-green-600 dark:text-green-400">
                            {companyStats.done_projects}
                          </span>
                          <span className="text-xs text-text-tertiary ml-1">Done</span>
                        </div>
                      )}
                    </dd>
                  </div>
                  {/* 30-day sparkline */}
                  {filteredProjectSparkline.length > 1 && (
                    <div
                      className="flex-shrink-0 -ml-6 relative"
                      style={{ width: 128, height: 32 }}
                      onMouseEnter={() => setSparklineHover(true)}
                      onMouseLeave={() => setSparklineHover(false)}
                    >
                      <ResponsiveLine
                        data={[{
                          id: 'projects',
                          data: filteredProjectSparkline.map((d) => ({
                            x: d.date,
                            y: d.count,
                          })),
                        }]}
                        margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 0, max: 'auto' }}
                        curve="monotoneX"
                        colors={['#6366f1']}
                        lineWidth={1.5}
                        enableArea={true}
                        areaOpacity={0.15}
                        enablePoints={false}
                        enableGridX={false}
                        enableGridY={false}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={null}
                        axisLeft={null}
                        isInteractive={false}
                      />
                      {sparklineHover && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 pointer-events-none">
                          <div className="bg-surface-primary dark:bg-gray-800 border border-border-primary shadow-lg rounded-lg px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-indigo-500"></div>
                              <span className="text-sm font-semibold text-text-primary">
                                {filteredProjectSparkline.reduce((sum, d) => sum + d.count, 0)}
                              </span>
                              <span className="text-xs text-text-tertiary">projects created</span>
                            </div>
                            <p className="text-[10px] text-text-tertiary mt-0.5 text-center">Last 30 days</p>
                          </div>
                          <div className="flex justify-center">
                            <div className="w-2 h-2 bg-surface-primary dark:bg-gray-800 border-r border-b border-border-primary rotate-45 -mt-1"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <svg className="h-5 w-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <dt className="text-sm font-medium text-text-tertiary truncate">Requirements (Tasks & Subtasks)</dt>
                    <dd className="mt-1 flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-text-primary">
                        {viewMode === 'all' ? (companyStats?.total_requirements ?? '--') : filteredTotals.total_requirements}
                      </span>
                      <span className={`text-xs font-medium ${completionRate >= 50 ? 'text-green-600 dark:text-green-400' : 'text-text-tertiary'}`}>
                        {completionRate}% complete
                      </span>
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription */}
            <div className="bg-surface-primary overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                      <svg className="h-5 w-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <dt className="text-sm font-medium text-text-tertiary truncate">Subscription</dt>
                    <dd className="mt-1">
                      <span className="text-lg font-semibold text-text-primary capitalize">
                        {company?.subscription_tier || '--'}
                      </span>
                      {company?.subscription_status === 'active' && (
                        <p className="text-xs text-green-600 dark:text-green-400 font-medium mt-0.5">Active</p>
                      )}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Active Sprint Countdown Cards */}
          {activeSprints.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeSprints.map((sprint) => {
                const daysLeft = getDaysUntil(sprint.end_date);
                const totalDays = Math.ceil(
                  (new Date(sprint.end_date).getTime() - new Date(sprint.start_date).getTime()) / (1000 * 60 * 60 * 24)
                );
                const elapsed = totalDays - daysLeft;
                const timeProgress = totalDays > 0 ? Math.min(Math.round((elapsed / totalDays) * 100), 100) : 0;

                return (
                  <div
                    key={sprint.id}
                    className="bg-surface-primary shadow rounded-lg p-5 border-l-4 border-indigo-500 dark:border-indigo-400 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate('/taskboard')}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-text-primary">{sprint.name}</h4>
                          {sprint.team_name && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
                              {sprint.team_name}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-text-tertiary mt-0.5">
                          {sprint.project_name && `${sprint.project_code} \u00b7 `}
                          Sprint #{sprint.sprint_number}
                        </p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        daysLeft <= 2 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                        daysLeft <= 5 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      }`}>
                        {daysLeft <= 0 ? 'Ends today' : `${daysLeft}d left`}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            timeProgress >= 80 ? 'bg-amber-500' : 'bg-indigo-500 dark:bg-indigo-400'
                          }`}
                          style={{ width: `${timeProgress}%` }}
                        />
                      </div>
                      <span className="text-xs text-text-tertiary whitespace-nowrap">{timeProgress}% elapsed</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-text-tertiary">
                      <span>{new Date(sprint.start_date).toLocaleDateString()}</span>
                      <span>{new Date(sprint.end_date).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Charts Row: Board Status (1/3) + Requirements Trend (2/3) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Board Status Bar Chart — compact 1/3 */}
            {leadershipData && (
              <div className="lg:col-span-1 bg-surface-primary shadow rounded-lg">
                <div className="px-5 py-4 border-b border-border-primary">
                  <h3 className="text-sm font-semibold text-text-primary">Board Status</h3>
                  <p className="text-xs text-text-tertiary mt-0.5">{filteredTotals.total_requirements} total</p>
                </div>
                <div className="px-4 py-3">
                  {filteredTotals.total_requirements > 0 ? (
                    <div style={{ height: 180 }}>
                      <ResponsiveBar
                        data={barChartData}
                        keys={['count']}
                        indexBy="status"
                        layout="horizontal"
                        margin={{ top: 4, right: 40, bottom: 4, left: 72 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={['#3b82f6']}
                        borderRadius={3}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={null}
                        axisLeft={{
                          tickSize: 0,
                          tickPadding: 8,
                        }}
                        enableLabel={false}
                        enableGridX={false}
                        enableGridY={false}
                        layers={[
                          'grid',
                          'axes',
                          'bars',
                          (props) => {
                            return (
                              <g>
                                {props.bars.map((bar) => {
                                  const value = bar.data.value ?? 0;
                                  const label = String(value);
                                  const labelWidth = label.length * 8 + 12;
                                  const labelHeight = 20;
                                  const x = bar.x + bar.width + 6;
                                  const y = bar.y + bar.height / 2;
                                  return (
                                    <g key={bar.key}>
                                      <rect
                                        x={x}
                                        y={y - labelHeight / 2}
                                        width={labelWidth}
                                        height={labelHeight}
                                        rx={5}
                                        ry={5}
                                        fill="#f3f4f6"
                                      />
                                      <text
                                        x={x + labelWidth / 2}
                                        y={y}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        style={{ fontSize: 11, fontWeight: 600, fill: '#374151' }}
                                      >
                                        {label}
                                      </text>
                                    </g>
                                  );
                                })}
                              </g>
                            );
                          },
                          'markers',
                          'annotations',
                        ]}
                        tooltip={({ indexValue, value }) => (
                          <div className="bg-surface-primary shadow-lg rounded-lg px-3 py-2 border border-border-primary">
                            <span className="text-sm font-medium text-text-primary">{indexValue}</span>
                            <span className="text-sm text-text-secondary ml-2">{value}</span>
                          </div>
                        )}
                        theme={{
                          axis: {
                            ticks: {
                              text: { fill: '#6b7280', fontSize: 12 },
                            },
                          },
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-[180px] text-text-tertiary">
                      <p className="text-sm">No data yet</p>
                    </div>
                  )}
                  {/* Backlog count below chart */}
                  {leadershipData && (
                    <div className="mt-2 pt-3 border-t border-border-primary">
                      <div className="flex items-center gap-3 px-1">
                        <span className="text-xs font-medium text-text-secondary">Backlog</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-700 dark:text-gray-300">
                          {filteredTotals.total_requirements -
                            filteredTotals.board_todo -
                            filteredTotals.board_in_progress -
                            filteredTotals.board_blocked -
                            filteredTotals.board_done -
                            filteredTotals.board_done_not_worked}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Requirements Created Over Time — 2/3 */}
            <div className="lg:col-span-2 bg-surface-primary shadow rounded-lg">
              <div className="px-5 py-4 border-b border-border-primary flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-text-primary">Requirements Created Over Time</h3>
                  <p className="text-xs text-text-tertiary mt-0.5">
                    {filteredTrendData.reduce((sum, d) => sum + d.count, 0)} created {trendDays === 'ytd' ? 'this year' : `in the last ${trendDays} days`}
                  </p>
                </div>
                <div className="flex items-center bg-background-secondary rounded-lg p-0.5">
                  {([30, 90, 'ytd'] as const).map((d) => (
                    <button
                      key={d}
                      onClick={() => {
                        setTrendDays(d);
                        loadTrend(d);
                      }}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                        trendDays === d
                          ? 'bg-surface-primary shadow text-text-primary'
                          : 'text-text-tertiary hover:text-text-secondary'
                      }`}
                    >
                      {d === 'ytd' ? 'YTD' : `${d}d`}
                    </button>
                  ))}
                </div>
              </div>
              <div className="px-4 py-3">
                {trendLoading ? (
                  <div className="flex items-center justify-center h-[210px]">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                  </div>
                ) : filteredTrendData.length > 0 ? (
                  <div style={{ height: 210 }}>
                    <ResponsiveLine
                      data={[
                        {
                          id: 'Requirements',
                          color: '#6366f1',
                          data: filteredTrendData.map((d) => ({
                            x: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                            y: d.count,
                          })),
                        },
                      ]}
                      margin={{ top: 10, right: 20, bottom: 30, left: 40 }}
                      xScale={{ type: 'point' }}
                      yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
                      curve="monotoneX"
                      colors={['#6366f1']}
                      lineWidth={2}
                      enableArea={true}
                      areaOpacity={0.08}
                      pointSize={filteredTrendData.length <= 30 ? 6 : 0}
                      pointColor="#6366f1"
                      pointBorderWidth={2}
                      pointBorderColor="#ffffff"
                      enableGridX={false}
                      enableGridY={true}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 0,
                        tickPadding: 8,
                        tickRotation: filteredTrendData.length > 15 ? -45 : 0,
                        tickValues: filteredTrendData.length > 20
                          ? filteredTrendData
                              .filter((_, i) => i % Math.ceil(filteredTrendData.length / 8) === 0)
                              .map((d) => new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
                          : undefined,
                      }}
                      axisLeft={{
                        tickSize: 0,
                        tickPadding: 8,
                        format: (v) => (Number.isInteger(v) ? String(v) : ''),
                      }}
                      useMesh={true}
                      enableSlices="x"
                      sliceTooltip={({ slice }) => (
                        <div className="bg-surface-primary shadow-lg rounded-lg px-3 py-2 border border-border-primary">
                          {slice.points.map((p) => (
                            <div key={p.id} className="text-sm">
                              <span className="text-text-secondary">{p.data.xFormatted}:</span>
                              <span className="font-semibold text-text-primary ml-2">{p.data.yFormatted} created</span>
                            </div>
                          ))}
                        </div>
                      )}
                      theme={{
                        axis: {
                          ticks: { text: { fill: '#6b7280', fontSize: 11 } },
                        },
                        grid: {
                          line: { stroke: '#e5e7eb', strokeWidth: 1, strokeDasharray: '4 4' },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[210px] text-text-tertiary">
                    <p className="text-sm">No requirements created in this period</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Grid: Left 2/3 (Project Health) + Right 1/3 (Blocked + Quick Actions + Activity) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column — 2/3 */}
            <div className="lg:col-span-2 space-y-6">
              {/* Project Health Table */}
              {filteredProjects.length > 0 && (
                <div className="bg-surface-primary shadow rounded-lg">
                  <div className="px-6 py-5 border-b border-border-primary flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-text-primary">Project Health Overview</h3>
                      <p className="mt-1 text-sm text-text-tertiary">
                        {sortedProjects.length} project{sortedProjects.length !== 1 ? 's' : ''} sorted by completion
                      </p>
                    </div>
                    {ownerOptions.length > 1 && (
                      <select
                        value={ownerFilter}
                        onChange={(e) => setOwnerFilter(e.target.value)}
                        className="text-sm border border-border-primary rounded-lg pl-3 pr-8 py-1.5 bg-surface-primary text-text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="all">All Owners</option>
                        {ownerOptions.map((name) => (
                          <option key={name} value={name!}>{name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border-primary">
                      <thead>
                        <tr className="bg-background-secondary">
                          <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">Project</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">30d Activity</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">Reqs</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider w-48">Progress</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-text-tertiary uppercase tracking-wider">Blocked</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">Owner</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-primary">
                        {sortedProjects.map((project) => (
                          <tr
                            key={project.id}
                            onClick={() => navigate(`/projects/${project.id}`)}
                            className="hover:bg-background-secondary dark:hover:bg-surface-secondary cursor-pointer transition-colors"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <p className="text-sm font-medium text-text-primary">{project.name}</p>
                                <p className="text-xs text-text-tertiary">{project.project_code}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                const trend = projectReqTrends[project.id];
                                if (!trend || trend.length === 0) {
                                  return <span className="text-xs text-text-tertiary">&mdash;</span>;
                                }
                                const total = trend.reduce((s, d) => s + d.count, 0);
                                let chartData = trend.map((d) => ({ x: d.date, y: d.count }));
                                if (chartData.length === 1) {
                                  const prevDay = new Date(chartData[0].x);
                                  prevDay.setDate(prevDay.getDate() - 1);
                                  chartData = [{ x: prevDay.toISOString().split('T')[0], y: 0 }, ...chartData];
                                }
                                return (
                                  <div className="flex items-center gap-2">
                                    <div style={{ width: 80, height: 24 }}>
                                      <ResponsiveLine
                                        data={[{
                                          id: 'reqs',
                                          data: chartData,
                                        }]}
                                        margin={{ top: 2, right: 2, bottom: 2, left: 2 }}
                                        xScale={{ type: 'point' }}
                                        yScale={{ type: 'linear', min: 0, max: 'auto' }}
                                        curve="monotoneX"
                                        colors={['#6366f1']}
                                        lineWidth={1.5}
                                        enableArea={true}
                                        areaOpacity={0.15}
                                        enablePoints={false}
                                        enableGridX={false}
                                        enableGridY={false}
                                        axisTop={null}
                                        axisRight={null}
                                        axisBottom={null}
                                        axisLeft={null}
                                        isInteractive={false}
                                      />
                                    </div>
                                    <span className="text-xs text-text-tertiary">{total}</span>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-sm font-medium text-text-primary">{project.total_requirements}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${getProgressBarColor(project.progress_percent)}`}
                                    style={{ width: `${Math.min(project.progress_percent, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-medium text-text-secondary w-10 text-right">
                                  {project.progress_percent}%
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {project.board_breakdown.blocked > 0 ? (
                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold">
                                  {project.board_breakdown.blocked}
                                </span>
                              ) : (
                                <span className="text-sm text-text-tertiary">&mdash;</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-text-secondary">{project.owner_name || '--'}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Right column — 1/3 */}
            <div className="lg:col-span-1 space-y-6">
              {/* Blocked Items Alert */}
              {filteredBlockedItems.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-red-200 dark:border-red-800/50">
                    <div className="flex items-center gap-2">
                      <svg className="h-5 w-5 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                        {filteredBlockedItems.length} Blocked Requirement{filteredBlockedItems.length !== 1 ? 's' : ''}
                      </h3>
                    </div>
                  </div>
                  <div className="px-6 py-3 max-h-64 overflow-y-auto">
                    <div className="space-y-3">
                      {filteredBlockedItems.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => navigate(`/taskboard/requirement/${item.id}`)}
                          className="flex items-start gap-3 p-2 -mx-2 rounded-md hover:bg-red-100 dark:hover:bg-red-900/20 cursor-pointer transition-colors"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono font-medium text-red-600 dark:text-red-400">
                                {item.requirement_code}
                              </span>
                            </div>
                            <p className="text-sm text-text-primary mt-0.5 truncate">{item.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-text-tertiary">{item.project_name}</span>
                              {item.owner_name && (
                                <>
                                  <span className="text-text-tertiary">&middot;</span>
                                  <span className="text-xs text-text-tertiary">{item.owner_name}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <svg className="h-4 w-4 text-red-400 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-surface-primary shadow rounded-lg p-5">
                <h3 className="text-sm font-semibold text-text-primary mb-3">Quick Actions</h3>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => navigate('/projects/new')}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-600 text-white text-sm font-medium hover:from-indigo-600 hover:to-blue-700 transition-all shadow-sm"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Project
                  </button>
                  <button
                    onClick={() => navigate('/users')}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 text-white text-sm font-medium hover:from-blue-600 hover:to-violet-700 transition-all shadow-sm"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Invite User
                  </button>
                  <button
                    onClick={() => navigate('/company/settings')}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-medium hover:from-violet-600 hover:to-purple-700 transition-all shadow-sm"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Company Settings
                  </button>
                </div>
              </div>

              {/* Activity Feed */}
              <div className="bg-surface-primary shadow rounded-lg">
                <div
                  className="px-6 py-5 border-b border-border-primary cursor-pointer"
                  onClick={toggleActivityExpanded}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg
                        className={`h-5 w-5 text-text-tertiary transition-transform duration-200 ${isActivityExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <h3 className="text-lg font-medium text-text-primary">Recent Activity</h3>
                    </div>
                    <span className="text-xs text-text-tertiary">{filteredActivities.length} events</span>
                  </div>
                </div>
                {isActivityExpanded && (
                  <div className="px-6 py-4 max-h-[480px] overflow-y-auto">
                    {filteredActivities.length === 0 ? (
                      <p className="text-sm text-text-tertiary text-center py-6">No recent activity</p>
                    ) : (
                      <div className="space-y-4">
                        {filteredActivities.slice(0, 15).map((activity) => {
                          const isUserAction = activity.action_type === 'user_created' || activity.action_type === 'user_invited';
                          return (
                            <div key={activity.id} className="pb-4 border-b border-border-primary last:border-0 last:pb-0">
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 mt-0.5">
                                  <span className={`h-7 w-7 rounded-full flex items-center justify-center ${
                                    isUserAction
                                      ? 'bg-blue-100 dark:bg-blue-900/30'
                                      : 'bg-indigo-100 dark:bg-indigo-900/30'
                                  }`}>
                                    {isUserAction ? (
                                      <svg className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                      </svg>
                                    ) : (
                                      <svg className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    )}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-text-primary">
                                    <span className="font-medium">
                                      {activity.user_first_name} {activity.user_last_name}
                                    </span>
                                    {' '}{activity.description}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1.5">
                                    {activity.project_code && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-background-secondary text-text-secondary">
                                        {activity.project_code}
                                      </span>
                                    )}
                                    <span className="text-xs text-text-tertiary">
                                      {getRelativeTime(activity.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default PMDashboardV2;
